import { useState } from 'react';
import { convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor, EditorProps } from 'react-draft-wysiwyg';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FieldLabel } from '@rossum/ui';
import type { FieldLabelProps } from '@rossum/ui';
import { FormHelperText, Stack } from '@rossum/ui/material';
import { useWysiwygHtmlDecorator } from '../../../../containers/Queue/components/TemplateMessageEditor/hooks';
import { ControlledFieldProps, LabeledFieldProps } from '../../utils';
import { wysiwygControlSx } from './wysiwygControlSx';
import editorToolbarConfig from './wysiwygControlToolbarConfig';

type WysiwygControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ControlledFieldProps<TFieldValues, TName> &
  LabeledFieldProps & {
    // this has to be passed explicitly now
    initialValue: string;
    disabled?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    required?: boolean;
    customButtons?: EditorProps['toolbarCustomButtons'];
  };

const WysiwygControl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  FieldLabelProps,
  initialValue,
  disabled,
  required,
  onFocus,
  onBlur,
  customButtons,
}: WysiwygControlProps<TFieldValues, TName>) => {
  const { control, name } = ControllerProps;

  const resolvedFieldLabelProps: FieldLabelProps = {
    htmlFor: name,
    required,
    ...FieldLabelProps,
    label: FieldLabelProps?.label ?? name,
  };

  const [, editorState, setEditorState] = useWysiwygHtmlDecorator({
    msgString: initialValue,
  });

  // oxlint-disable-next-line react-ext/hook-use-state -- write-only state, value intentionally unused
  const [_focused, setFocused] = useState(false);

  const wysiwygFocused = () => {
    if (!disabled) {
      setFocused(true);
      onFocus?.();
    }
  };

  const wysiwygBlurred = () => {
    if (!disabled) {
      setFocused(false);
      onBlur?.();
    }
  };

  return (
    <FieldLabel {...resolvedFieldLabelProps}>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => (
          <Stack
            spacing={1}
            sx={wysiwygControlSx({
              toolbarDisabled: disabled,
              contentDisabled: formState.isSubmitting || disabled,
            })}
          >
            <Editor
              editorRef={field.ref}
              editorState={editorState}
              onFocus={() => wysiwygFocused()}
              onBlur={() => wysiwygBlurred()}
              onEditorStateChange={newEditorState => {
                field.onChange(
                  draftToHtml(convertToRaw(newEditorState.getCurrentContent()))
                );
                setEditorState(newEditorState);
              }}
              stripPastedStyles
              toolbar={editorToolbarConfig}
              readOnly={formState.isSubmitting || disabled}
              toolbarCustomButtons={customButtons ?? []}
            />
            {fieldState?.error && (
              <FormHelperText error>{fieldState.error?.message}</FormHelperText>
            )}
          </Stack>
        )}
      />
    </FieldLabel>
  );
};

export const WysiwygControl2 = ({
  value,
  onChange,
  disabled,
  customButtons,
}: {
  value: EditorState;
  onChange: (editorState: EditorState) => void;
  disabled?: boolean;
  customButtons?: EditorProps['toolbarCustomButtons'];
}) => {
  return (
    <Stack
      sx={wysiwygControlSx({
        toolbarDisabled: disabled,
        contentDisabled: disabled,
      })}
    >
      <Editor
        editorState={value}
        onEditorStateChange={editorState => {
          if (!disabled) {
            onChange(editorState);
          }
        }}
        stripPastedStyles
        toolbar={editorToolbarConfig}
        readOnly={disabled}
        toolbarCustomButtons={customButtons ?? []}
      />
    </Stack>
  );
};

export default WysiwygControl;
